import { groupBy, keyBy } from 'lodash/fp';

import { ScreenDimensions } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { AccountTypesFiltersResponse } from 'dashboard/models/account-type';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionSchema,
} from 'dashboard/models/inquiry-template-version';
import { VerificationTemplateVersionStubs } from 'dashboard/models/simulate/stubs';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  setAccountTypesFilterOptions,
  setInquiry,
  setInquiryTemplate,
  setInquiryTemplateVersion,
  setInquiryTemplateVersionSchema,
  setPreviewScreenDimensions,
} from 'dashboard/redux/slices/inquiry-preview';
import { updateScreenDimensionsInLocalStorage } from 'dashboard/redux/thunk-actions/inquiry-template';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { FieldValue } from 'lib/fields';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';

async function saveStubs(
  inquiryId: string,
  sandboxEnvId: string,
  verificationTemplateVersionStubs: VerificationTemplateVersionStubs[],
  fieldsStubs: Record<string, FieldValue>,
): Promise<Inquiry> {
  const { data: inquiry } = await SessionClientDashboard.updateResourceAction<Inquiry>(
    { id: inquiryId, type: ApiModel.Inquiry },
    'set-verification-template-version-stubs',
    {
      meta: {
        'verification-template-version-stubs': verificationTemplateVersionStubs,
        'fields-stubs': fieldsStubs,
      },
      headers: {
        'Persona-Environment-ID': sandboxEnvId,
      },
    },
  );
  return inquiry;
}

export const saveVerificationTemplateVersionStubs =
  (
    inquiryId: string,
    sandboxEnvId: string,
    verificationTemplateVersionStubs: VerificationTemplateVersionStubs[],
    fieldsStubs: Record<string, FieldValue>,
  ): DashboardThunkAction =>
  async (dispatch) => {
    const inquiry = await saveStubs(
      inquiryId,
      sandboxEnvId,
      verificationTemplateVersionStubs,
      fieldsStubs,
    );
    dispatch(setInquiry({ inquiry }));
  };

export const saveNextStepName =
  (nextStepName: string, inquiryId: string, sandboxEnvId: string): DashboardThunkAction =>
  async () => {
    await SessionClientDashboard.updateResourceAction<Inquiry>(
      { id: inquiryId, type: ApiModel.Inquiry },
      'set-next-step-name',
      {
        meta: {
          'next-step-name': nextStepName,
        },
        headers: {
          'Persona-Environment-ID': sandboxEnvId,
        },
      },
    );
  };

export const createSandboxInquiry =
  ({
    inquiryTemplateId,
    inquiryTemplateVersionId,
    referenceId,
    accountTypeId,
    fields,
    sandboxEnvId,
  }: {
    inquiryTemplateId: string;
    inquiryTemplateVersionId: string;
    referenceId?: string;
    accountTypeId?: string;
    fields?: Record<string, unknown>;
    sandboxEnvId: string;
  }): DashboardThunkAction =>
  async (dispatch) => {
    const { data: inquiry } = await SessionClientDashboard.postResourceCreate<
      Inquiry,
      {
        type: ApiModel.Inquiry;
        attributes: {
          'inquiry-template-version-id': string;
          'inquiry-template-id': string;
          'reference-id': string | undefined;
          'account-type-id': string | undefined;
          fields: Record<string, unknown>;
        };
      }
    >(
      {
        type: ApiModel.Inquiry,
        attributes: {
          'inquiry-template-id': inquiryTemplateId,
          'inquiry-template-version-id': inquiryTemplateVersionId,
          'reference-id': referenceId,
          'account-type-id': accountTypeId,
          fields: fields || {},
        },
      },
      {
        headers: {
          'Persona-Environment-ID': sandboxEnvId,
        },
      },
    );
    dispatch(setInquiry({ inquiry }));
  };

export const unsetSimulateInquiry = (): DashboardThunkAction => async (dispatch) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  dispatch(setInquiry({ inquiry: null }));
};

export const loadInquiry =
  (inquiryId: string, sandboxEnvId: string, abortSignal?: AbortSignal): DashboardThunkAction =>
  async (dispatch) => {
    try {
      const inquiryResponse = await SessionClientDashboard.getResource<Inquiry>(
        ApiEndpoint.Inquiry,
        inquiryId,
        {
          headers: {
            'Persona-Environment-ID': sandboxEnvId,
          },
          signal: abortSignal,
        },
      );
      const inquiry = inquiryResponse.data;
      dispatch(setInquiry({ inquiry }));
    } catch (err) {
      if (abortSignal == null || !abortSignal.aborted) {
        throw err;
      }
    }
  };

export const loadInquiryAndTemplateData =
  (inquiryId: string, sandboxEnvId: string): DashboardThunkAction =>
  async (dispatch) => {
    const inquiryResponse = await SessionClientDashboard.getResource<Inquiry>(
      ApiEndpoint.Inquiry,
      inquiryId,
      {
        headers: {
          'Persona-Environment-ID': sandboxEnvId,
        },
      },
    );
    const inquiry = inquiryResponse.data;
    dispatch(setInquiry({ inquiry }));

    // Could be a 2.0 inquiry, in which case, don't attempt to fetch ITV
    const inquiryTemplateVersionId =
      inquiryResponse.data.relationships?.['inquiry-template-version']?.data?.id;
    if (inquiryTemplateVersionId) {
      await dispatch(loadInquiryTemplateAndAccountTypesData(inquiryTemplateVersionId));
      // Display message only if we're able to successfully load the template
      dispatch(showMessage('Loaded sandbox inquiry for preview.', MessageType.Success));
    }
  };

async function fetchInquiryTemplateAndVersion(
  inquiryTemplateVersionId: string,
): Promise<[InquiryTemplateVersion, InquiryTemplate]> {
  const { data: inquiryTemplateVersion, included } =
    await SessionClientDashboard.getResource<InquiryTemplateVersion>(
      ApiEndpoint.InquiryTemplateVersion,
      inquiryTemplateVersionId,
    );
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const inquiryTemplate = included.find(
    (entity) => entity.type === ApiModel.InquiryTemplate,
  ) as InquiryTemplate;

  return [inquiryTemplateVersion, inquiryTemplate];
}

async function fetchSchema(
  inquiryTemplateVersionId: string,
): Promise<InquiryTemplateVersionSchema> {
  const { meta: schema } = await SessionClientDashboard.getResource<
    InquiryTemplateVersion,
    InquiryTemplateVersionSchema
  >(ApiEndpoint.InquiryTemplateVersion, `${inquiryTemplateVersionId}/schema`);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return schema;
}

const fetchAccountTypesFilterOptions = async () => {
  const { meta } = await SessionClientDashboard.get<AccountTypesFiltersResponse>(
    ApiEndpoint.AccountType,
    'basics',
    '',
    {},
  );

  return meta.filters;
};

export const loadInquiryTemplateAndAccountTypesData =
  (inquiryTemplateVersionId: string): DashboardThunkAction =>
  async (dispatch) => {
    const [
      [inquiryTemplateVersion, inquiryTemplate],
      inquiryTemplateVersionSchema,
      accountTypesFilterOptions,
    ] = await Promise.all([
      fetchInquiryTemplateAndVersion(inquiryTemplateVersionId),
      fetchSchema(inquiryTemplateVersionId),
      fetchAccountTypesFilterOptions(),
    ]);

    dispatch(setAccountTypesFilterOptions({ accountTypesFilterOptions }));
    dispatch(setInquiryTemplate({ inquiryTemplate }));
    dispatch(setInquiryTemplateVersion({ inquiryTemplateVersion }));
    dispatch(
      setInquiryTemplateVersionSchema({
        inquiryTemplateVersionSchema: {
          workflowVersions: keyBy('id', inquiryTemplateVersionSchema['workflow-versions'].data),
          verificationTemplateVersions: keyBy(
            'id',
            inquiryTemplateVersionSchema['verification-template-versions'].data,
          ),
          matchRequirementProfiles: groupBy(
            'object-id',
            inquiryTemplateVersionSchema['match-requirement-profiles'].data,
          ),
        },
      }),
    );
  };

/*
 * Screen Dimension Dispatch Function
 * This dispatch function should be in sync with the dispatch function in:
 * front-end/dashboard/redux/thunk-actions/inquiry-template.ts
 * If you change one, please check if the other needs changes as well
 */

// Follows the same logic as InquiryTemplateEditor's updateEditorScreenDimensions
// Only difference is that we are reading the inquiry template id from the inquiry preview state
export const updatePreviewScreenDimensions =
  (screenDimensions: ScreenDimensions): DashboardThunkAction =>
  (dispatch, getState) => {
    const state = getState();

    const inquiryTemplateId = state.inquiryPreview?.inquiryTemplate?.id;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    updateScreenDimensionsInLocalStorage(inquiryTemplateId, screenDimensions);
    dispatch(setPreviewScreenDimensions({ screenDimensions }));
  };
